import React, { useState, useEffect } from 'react';
import collegeConfig from '../../config/collegeConfig';
import {
  FaUserTie, FaPlus, FaEdit, FaTrash, FaSearch, FaEnvelope,
  FaTimes, FaCheckCircle, FaTimesCircle, FaSave, FaLock,
  FaSpinner, FaEye, FaEyeSlash, FaInfoCircle
} from 'react-icons/fa';

const API = collegeConfig.api.baseURL;
const token = () => localStorage.getItem('token');
const headers = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` });

const ManageDeanUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '', isActive: true });
  const [pwForm, setPwForm] = useState({ newPassword: '', confirmPassword: '' });

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/students/all-users?role=dean`, { headers: { Authorization: `Bearer ${token()}` } });
      const d = await r.json();
      if (d.success) setUsers(d.users || []);
    } catch { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { fetchUsers(); }, []);

  const openCreate = () => {
    setForm({ firstName: '', lastName: '', email: '', password: '', isActive: true });
    setModal('create');
  };

  const openEdit = (u) => {
    setSelected(u);
    setForm({ firstName: u.profile?.firstName || '', lastName: u.profile?.lastName || '', email: u.email || '', isActive: u.isActive !== false, password: '' });
    setModal('edit');
  };

  const openPassword = (u) => { setSelected(u); setPwForm({ newPassword: '', confirmPassword: '' }); setModal('password'); };

  const handleCreate = async () => {
    const { firstName, lastName, email, password } = form;
    if (!firstName || !lastName || !email || !password) { showToast('All fields are required'); return; }
    if (password.length < 6) { showToast('Password must be at least 6 characters'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/students/create-user`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ email, password, role: 'dean', profile: { firstName, lastName } })
      });
      const d = await r.json();
      if (d.success) { showToast('Dean account created'); setModal(null); fetchUsers(); }
      else showToast(d.message || 'Failed to create');
    } catch { showToast('Failed to create'); }
    setSaving(false);
  };

  const handleEdit = async () => {
    const { firstName, lastName, email, isActive } = form;
    if (!firstName || !lastName || !email) { showToast('Name and email are required'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/students/update-user/${selected._id}`, {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ email, profile: { ...selected.profile, firstName, lastName }, isActive })
      });
      const d = await r.json();
      if (d.success) { showToast('Updated successfully'); setModal(null); fetchUsers(); }
      else showToast(d.message || 'Failed to update');
    } catch { showToast('Failed to update'); }
    setSaving(false);
  };

  const handleChangePassword = async () => {
    if (!pwForm.newPassword || pwForm.newPassword.length < 6) { showToast('Password must be at least 6 characters'); return; }
    if (pwForm.newPassword !== pwForm.confirmPassword) { showToast('Passwords do not match'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/students/update-user/${selected._id}`, {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ password: pwForm.newPassword })
      });
      const d = await r.json();
      if (d.success) { showToast('Password changed'); setModal(null); }
      else showToast(d.message || 'Failed');
    } catch { showToast('Failed'); }
    setSaving(false);
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete dean account for "${u.profile?.firstName} ${u.profile?.lastName}"?`)) return;
    try {
      const r = await fetch(`${API}/students/delete-user/${u._id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token()}` } });
      const d = await r.json();
      if (d.success) { showToast('Deleted'); fetchUsers(); }
      else showToast(d.message || 'Failed');
    } catch { showToast('Failed'); }
  };

  const filtered = users.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    const name = `${u.profile?.firstName || ''} ${u.profile?.lastName || ''}`.toLowerCase();
    return name.includes(q) || (u.email || '').toLowerCase().includes(q);
  });

  return (
    <div style={s.container}>
      {toast && <div style={s.toast}>{toast}</div>}

      <div style={s.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <FaUserTie style={{ fontSize: 24, color: '#1a5276' }} />
          <div>
            <h1 style={s.title}>Manage Dean Users</h1>
            <p style={s.subtitle}>College-wide oversight users who can request entry reattempts across any department</p>
          </div>
        </div>
        <button onClick={openCreate} style={s.createBtn}><FaPlus style={{ marginRight: 6 }} />Create Dean</button>
      </div>

      {/* Info banner */}
      <div style={s.infoBanner}>
        <FaInfoCircle style={{ color: '#2563eb', flexShrink: 0 }} />
        <div style={{ fontSize: 13 }}>
          <strong>Dean Role:</strong> Deans can browse logbook entries across any department and batch (month-wise, with date filters) and can request a reattempt on any eligible entry. Deans cannot verify or grade entries — that remains with faculty and HODs.
        </div>
      </div>

      {/* Stats */}
      <div style={s.statsRow}>
        <div style={s.statCard}><span style={{ ...s.statNum, color: '#1a5276' }}>{users.length}</span><span style={s.statLabel}>Total Deans</span></div>
        <div style={s.statCard}><span style={{ ...s.statNum, color: '#059669' }}>{users.filter(u => u.isActive !== false).length}</span><span style={s.statLabel}>Active</span></div>
      </div>

      <div style={s.searchWrap}>
        <FaSearch style={{ position: 'absolute', left: 12, top: 11, color: '#999' }} />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email..." style={s.searchInput} />
      </div>

      {loading ? (
        <div style={s.centered}><FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: 28, color: '#1a5276' }} /></div>
      ) : filtered.length === 0 ? (
        <div style={s.emptyState}>
          <FaUserTie style={{ fontSize: 48, color: '#ddd', marginBottom: 12 }} />
          <p style={{ color: '#999' }}>No dean accounts yet.</p>
          <p style={{ color: '#bbb', fontSize: 13 }}>Create a dean account to give someone college-wide reattempt oversight.</p>
          <button onClick={openCreate} style={{ ...s.createBtn, marginTop: 16 }}><FaPlus style={{ marginRight: 6 }} />Create First Dean</button>
        </div>
      ) : (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>{['Dean', 'Email', 'Status', 'Created', 'Actions'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {filtered.map(u => {
                const name = `${u.profile?.firstName || ''} ${u.profile?.lastName || ''}`.trim();
                return (
                  <tr key={u._id} style={s.tr}>
                    <td style={s.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={s.avatar}>{name.charAt(0).toUpperCase() || 'D'}</div>
                        <span style={{ fontWeight: 600 }}>{name || '—'}</span>
                      </div>
                    </td>
                    <td style={s.td}><FaEnvelope size={11} style={{ marginRight: 6, color: '#888' }} />{u.email}</td>
                    <td style={s.td}>
                      <span style={{ ...s.badge, background: u.isActive !== false ? '#d6eaf8' : '#fee2e2', color: u.isActive !== false ? '#1a5276' : '#dc2626' }}>
                        {u.isActive !== false ? <FaCheckCircle style={{ marginRight: 4 }} /> : <FaTimesCircle style={{ marginRight: 4 }} />}
                        {u.isActive !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={s.td}>{u.createdAt ? new Date(u.createdAt).toLocaleDateString('en-IN') : '—'}</td>
                    <td style={s.td}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => openEdit(u)} style={s.iconBtn} title="Edit"><FaEdit /></button>
                        <button onClick={() => openPassword(u)} style={{ ...s.iconBtn, color: '#1a5276' }} title="Change Password"><FaLock /></button>
                        <button onClick={() => handleDelete(u)} style={{ ...s.iconBtn, color: '#dc2626' }} title="Delete"><FaTrash /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {modal === 'create' && (
        <Modal title="Create Dean Account" onClose={() => setModal(null)}>
          <FormFields form={form} setForm={setForm} fields={[
            { key: 'firstName', label: 'First Name', required: true },
            { key: 'lastName', label: 'Last Name', required: true },
            { key: 'email', label: 'Email', type: 'email', required: true },
            { key: 'password', label: 'Password', type: 'password', required: true, placeholder: 'Min 6 characters' }
          ]} />
          <Actions onSave={handleCreate} onCancel={() => setModal(null)} saving={saving} saveLabel="Create Account" />
        </Modal>
      )}

      {/* Edit Modal */}
      {modal === 'edit' && (
        <Modal title={`Edit — ${selected?.profile?.firstName || ''}`} onClose={() => setModal(null)}>
          <FormFields form={form} setForm={setForm} fields={[
            { key: 'firstName', label: 'First Name', required: true },
            { key: 'lastName', label: 'Last Name', required: true },
            { key: 'email', label: 'Email', type: 'email', required: true }
          ]} />
          <div style={{ marginBottom: 14 }}>
            <label style={s.label}>Status</label>
            <select value={form.isActive ? 'active' : 'inactive'} onChange={e => setForm(p => ({ ...p, isActive: e.target.value === 'active' }))} style={s.input}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <Actions onSave={handleEdit} onCancel={() => setModal(null)} saving={saving} saveLabel="Save Changes" />
        </Modal>
      )}

      {/* Password Modal */}
      {modal === 'password' && (
        <Modal title={`Change Password — ${selected?.profile?.firstName || ''}`} onClose={() => setModal(null)}>
          <div style={{ marginBottom: 14 }}>
            <label style={s.label}>New Password *</label>
            <div style={{ position: 'relative' }}>
              <input type={showPw ? 'text' : 'password'} value={pwForm.newPassword} onChange={e => setPwForm(p => ({ ...p, newPassword: e.target.value }))} placeholder="Min 6 characters" style={{ ...s.input, paddingRight: 40 }} />
              <button onClick={() => setShowPw(!showPw)} style={{ position: 'absolute', right: 10, top: 10, background: 'none', border: 'none', cursor: 'pointer', color: '#666' }}>
                {showPw ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={s.label}>Confirm Password *</label>
            <input type="password" value={pwForm.confirmPassword} onChange={e => setPwForm(p => ({ ...p, confirmPassword: e.target.value }))} style={s.input} />
          </div>
          <Actions onSave={handleChangePassword} onCancel={() => setModal(null)} saving={saving} saveLabel="Change Password" color="#1a5276" />
        </Modal>
      )}

      <style>{`@keyframes spin { from{transform:rotate(0deg);}to{transform:rotate(360deg);}}`}</style>
    </div>
  );
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const Modal = ({ title, onClose, children }) => (
  <div style={s.overlay} onClick={onClose}>
    <div style={s.modalBox} onClick={e => e.stopPropagation()}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #e5e7eb' }}>
        <span style={{ fontWeight: 700, fontSize: 16 }}>{title}</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#666' }}><FaTimes /></button>
      </div>
      <div style={{ padding: 20 }}>{children}</div>
    </div>
  </div>
);

const FormFields = ({ form, setForm, fields }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 16 }}>
    {fields.map(f => (
      <div key={f.key}>
        <label style={s.label}>{f.label}{f.required && ' *'}</label>
        <input type={f.type || 'text'} value={form[f.key] || ''} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.placeholder} style={s.input} />
      </div>
    ))}
  </div>
);

const Actions = ({ onSave, onCancel, saving, saveLabel, color = '#1a5276' }) => (
  <div style={{ display: 'flex', gap: 10 }}>
    <button onClick={onSave} disabled={saving} style={{ ...s.createBtn, background: color }}>
      {saving ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <><FaSave style={{ marginRight: 6 }} />{saveLabel}</>}
    </button>
    <button onClick={onCancel} style={s.secondaryBtn}>Cancel</button>
  </div>
);

const s = {
  container: { padding: '0 0 32px', fontFamily: 'sans-serif' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  title: { margin: 0, fontSize: 22, fontWeight: 800, color: '#1a1a2e' },
  subtitle: { margin: '4px 0 0', fontSize: 13, color: '#666' },
  createBtn: { display: 'flex', alignItems: 'center', background: '#1a5276', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', cursor: 'pointer', fontWeight: 700, fontSize: 14 },
  infoBanner: { display: 'flex', gap: 10, padding: 14, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, marginBottom: 20 },
  statsRow: { display: 'flex', gap: 12, marginBottom: 20 },
  statCard: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 24px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  statNum: { display: 'block', fontSize: 26, fontWeight: 800, color: '#1a1a2e' },
  statLabel: { fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' },
  searchWrap: { position: 'relative', marginBottom: 16 },
  searchInput: { width: '100%', padding: '9px 12px 9px 36px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none' },
  centered: { display: 'flex', justifyContent: 'center', padding: 40 },
  emptyState: { textAlign: 'center', padding: '60px 20px' },
  tableWrap: { background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'auto', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 500 },
  th: { padding: '12px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' },
  tr: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '12px 14px', fontSize: 14, color: '#374151', verticalAlign: 'middle' },
  avatar: { width: 34, height: 34, borderRadius: '50%', background: '#1a5276', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0 },
  badge: { display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600 },
  iconBtn: { background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 8px', cursor: 'pointer', color: '#666', display: 'flex', alignItems: 'center' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
  modalBox: { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 },
  input: { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none' },
  secondaryBtn: { background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 14 },
  toast: { position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#1a1a2e', color: '#fff', padding: '10px 20px', borderRadius: 20, fontSize: 14, zIndex: 2000, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }
};

export default ManageDeanUsers;
